import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import DirectionsRunOutlinedIcon from '@mui/icons-material/DirectionsRunOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { Link } from 'react-router-dom';
import type { SxProps, Theme } from '@mui/material/styles';
import { BASE_API_PATH, BLOG_PATH, FEATURED_COLLECTION_ID, FEATURED_PHOTOS_PATH, PHOTOS_PATH } from '../constants';

const INTERESTS = [
    {
        label: 'Learning',
        text: 'from new people and perspectives.',
        icon: <MenuBookOutlinedIcon sx={{ color: '#2056b4' }} />,
    },
    {
        label: 'Building',
        text: 'ideas into reality, one step at a time.',
        icon: <AutoAwesomeOutlinedIcon sx={{ color: '#2056b4' }} />,
    },
    {
        label: 'Exploring',
        text: 'new places and adventures.',
        icon: <DirectionsRunOutlinedIcon sx={{ color: '#2056b4' }} />,
    },
    {
        label: 'Capturing',
        text: 'the moments that make life special.',
        icon: <CameraAltOutlinedIcon sx={{ color: '#2056b4' }} />,
    },
];

const HERO_BANNER_URL =
    '/hero-image.jpeg';

type FeedItem = {
    id: string;
    title: string;
    summary: string;
    timestamp: number;
    imageUrl: string;
    contentType: 'blog' | 'photo_collection';
    href?: string;
};

interface FeedResponse {
    items: FeedItem[];
}

interface FeaturedImage {
    id: string;
    imageUrl: string;
    sortOrder: number;
    focalPointX?: number;
    focalPointY?: number;
    squareFocalPointX?: number;
    squareFocalPointY?: number;
}

interface FeaturedCollectionResponse {
    id: string;
    galleryImages: FeaturedImage[];
}

interface LoadingImageProps {
    src: string;
    alt: string;
    frameSx: SxProps<Theme>;
    imgSx: SxProps<Theme>;
    minSkeletonMs?: number;
}

function LoadingImage({ src, alt, frameSx, imgSx, minSkeletonMs = 180 }: LoadingImageProps) {
    const [loaded, setLoaded] = useState(false);
    const loadedRef = useRef(false);
    const loadStartedAtRef = useRef(Date.now());
    const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);

    const markLoaded = () => {
        if (loadedRef.current || loadTimeoutRef.current) {
            return;
        }
        const elapsed = Date.now() - loadStartedAtRef.current;
        const remaining = Math.max(0, minSkeletonMs - elapsed);
        loadTimeoutRef.current = setTimeout(() => {
            loadedRef.current = true;
            setLoaded(true);
            loadTimeoutRef.current = null;
        }, remaining);
    };

    useEffect(() => {
        loadedRef.current = false;
        setLoaded(false);
        loadStartedAtRef.current = Date.now();
        if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current);
            loadTimeoutRef.current = null;
        }
        if (imageRef.current?.complete) {
            markLoaded();
        }
    }, [src, minSkeletonMs]);

    useEffect(() => {
        return () => {
            if (loadTimeoutRef.current) {
                clearTimeout(loadTimeoutRef.current);
            }
        };
    }, []);

    return (
        <Box sx={{ position: 'relative', overflow: 'hidden', ...frameSx }}>
            {!loaded && (
                <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', bgcolor: '#e7f0fd' }}
                />
            )}
            <Box
                component="img"
                ref={imageRef}
                src={src}
                alt={alt}
                onLoad={markLoaded}
                onError={markLoaded}
                sx={{ ...imgSx, opacity: loaded ? 1 : 0, transition: 'opacity 160ms ease' }}
            />
        </Box>
    );
}

function timestampToHumanReadableDate(timestamp: number) {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export function Home() {
    const [recentPosts, setRecentPosts] = useState<FeedItem[]>([]);
    const [recentPostsError, setRecentPostsError] = useState(false);
    const [featuredPhotos, setFeaturedPhotos] = useState<FeaturedImage[]>([]);
    const [featuredPhotosError, setFeaturedPhotosError] = useState(false);

    useEffect(() => {
        document.title = 'Mike Brauninger';
        ReactGA.send({ hitType: 'pageview', page: window.location.pathname });
    }, []);

    useEffect(() => {
        const getRecentPosts = async () => {
            try {
                const response = await fetch(`${BASE_API_PATH}/feed?limit=5`);
                if (!response.ok) {
                    throw new Error('Failed to fetch posts');
                }
                const data: FeedResponse = await response.json();
                setRecentPosts(data.items.slice(0, 5));
            } catch (error) {
                setRecentPostsError(true);
            }
        };

        getRecentPosts();
    }, []);

    useEffect(() => {
        const getFeaturedPhotos = async () => {
            try {
                const response = await fetch(`${BASE_API_PATH}/photos/${FEATURED_COLLECTION_ID}`);
                if (!response.ok) {
                    throw new Error('Failed to fetch featured photos');
                }
                const data: FeaturedCollectionResponse = await response.json();
                const sortedImages = [...(data.galleryImages || [])].sort(
                    (left, right) => left.sortOrder - right.sortOrder,
                );
                setFeaturedPhotos(sortedImages.slice(0, 6));
                setFeaturedPhotosError(false);
            } catch (error) {
                setFeaturedPhotosError(true);
            }
        };

        getFeaturedPhotos();
    }, []);

    return (
        <Box sx={{ pb: 4 }}>
            <Box
                sx={{
                    width: '100%',
                    minHeight: { xs: '42vh', md: '50vh' },
                    mt: {
                        xs: 'calc(-84px - env(safe-area-inset-top, 0px))',
                        md: 'calc(-94px - env(safe-area-inset-top, 0px))',
                    },
                    pt: {
                        xs: 'calc(110px + env(safe-area-inset-top, 0px))',
                        md: 'calc(130px + env(safe-area-inset-top, 0px))',
                    },
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundImage: `url(${HERO_BANNER_URL})`,
                    backgroundSize: { xs: 'auto 150%', md: 'cover' },
                    backgroundPosition: { xs: '80% 10%', md: '74% 18%', lg: '72% 16%' },
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: {
                            xs: 'linear-gradient(90deg, rgba(234,244,255,0.96) 0%, rgba(234,244,255,0.88) 22%, rgba(234,244,255,0.72) 36%, rgba(234,244,255,0.52) 46%, rgba(234,244,255,0.3) 54%, rgba(234,244,255,0.14) 60%, rgba(234,244,255,0.05) 66%, rgba(234,244,255,0) 72%), linear-gradient(180deg, rgba(234,244,255,0) 52%, rgba(234,244,255,0.58) 70%, rgba(234,244,255,0.95) 100%)',
                            md: 'linear-gradient(90deg, rgba(234,244,255,0.96) 0%, rgba(234,244,255,0.88) 22%, rgba(234,244,255,0.72) 36%, rgba(234,244,255,0.52) 46%, rgba(234,244,255,0.3) 54%, rgba(234,244,255,0.14) 60%, rgba(234,244,255,0.05) 66%, rgba(234,244,255,0) 72%)',
                        },
                    }}
                />
                <Box
                    sx={{
                        maxWidth: 1200,
                        mx: 'auto',
                        px: { xs: 2, md: 4 },
                        pb: { xs: 3.5, md: 6 },
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={7} sx={{ maxWidth: { xs: '62%', sm: '72%', md: '100%' } }}>
                            <Typography fontSize={18} color="#24509b" fontWeight={600} gutterBottom>
                                Hey there
                            </Typography>
                            <Typography
                                fontSize={{ xs: 42, sm: 52, md: 60 }}
                                lineHeight={1.05}
                                fontWeight={700}
                                color="#0f2f68"
                                gutterBottom
                            >
                                I&apos;m Mike.
                            </Typography>
                            <Typography fontSize={{ xs: 20, md: 28 }} color="#1f4e95" gutterBottom>
                                Welcome to my website.
                            </Typography>
                            <Typography color="#2e4f80" sx={{ maxWidth: 560, mb: 3, fontSize: { xs: 15, md: 16 } }}>
                                This is where I share moments from my life and other things that I care
                                about!
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                                <Button variant="contained" component={Link} to={`/${PHOTOS_PATH}`}>
                                    See my photos
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, pb: { xs: 4, md: 6 } }}>
                <Box
                    id="about-section"
                    sx={{
                        mt: 2,
                        borderRadius: 3,
                        p: { xs: 2.5, md: 3 },
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 3,
                        alignItems: { xs: 'stretch', md: 'center' },
                        backgroundColor: '#f8fbff',
                        border: '1px solid #d9e6f7',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'flex-start', md: 'center' },
                            width: { xs: '100%', md: '40%' },
                        }}
                    >
                        <LoadingImage
                            src="ProfilePic.png"
                            alt="Mike Brauninger"
                            frameSx={{
                                height: 110,
                                width: 110,
                                borderRadius: '50%',
                                border: '2px solid #d1e2fb',
                                flexShrink: 0,
                            }}
                            imgSx={{
                                height: '100%',
                                width: '100%',
                                objectFit: 'cover',
                                display: 'block',
                            }}
                        />
                        <Box sx={{ width: '100%' }}>
                            <Typography variant="h5" color="#143b7e" fontWeight={700}>
                                A little about me
                            </Typography>
                            <Typography color="#2d4f84" sx={{ mt: 0.75 }}>
                                I grew up Massachusetts and I've been living in Seattle for the past few years now. Here are a few themes that shape how I live my life.
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            width: { xs: '100%', md: '60%' },
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, minmax(0, 1fr))' },
                            gap: 1.5,
                        }}
                    >
                        {INTERESTS.map((interest) => (
                            <Box
                                key={interest.label}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2.5,
                                    backgroundColor: '#f7faff',
                                    border: '1px solid #d9e4f6',
                                    minHeight: 112,
                                }}
                            >
                                <Box sx={{ mb: 0.6 }}>{interest.icon}</Box>
                                <Typography fontWeight={700} color="#183f82" fontSize={14}>
                                    {interest.label}
                                </Typography>
                                <Typography color="#3b5a89" fontSize={12.5}>
                                    {interest.text}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                p: 2.5,
                                borderRadius: 3,
                                backgroundColor: '#f7fbff',
                                border: '1px solid #d9e7f8',
                            }}
                        >
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="h6" color="#163d81" fontWeight={700}>
                                    Post history
                                </Typography>
                            </Box>
                            <Stack spacing={1.5}>
                                {recentPostsError ? (
                                    <Typography color="#55729f" fontSize={14}>
                                        Could not load recent posts right now.
                                    </Typography>
                                ) : (
                                    recentPosts.map((post, index) => (
                                        <Box
                                            key={post.id}
                                            component={Link}
                                            to={
                                                post.contentType === 'blog'
                                                    ? (post.href?.replace(/^\/blog(?=\/|$)/, `/${BLOG_PATH}`) ??
                                                        `/${BLOG_PATH}/post?id=${post.id}`)
                                                    : (post.href ?? `/${PHOTOS_PATH}`)
                                            }
                                            sx={{
                                                borderBottom:
                                                    index === recentPosts.length - 1 ? 'none' : '1px solid #e3ecf9',
                                                pb: index === recentPosts.length - 1 ? 0 : 1.1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.1,
                                                textDecoration: 'none',
                                            }}
                                        >
                                            <LoadingImage
                                                src={post.imageUrl}
                                                alt={post.title}
                                                frameSx={{
                                                    width: 82,
                                                    height: 59,
                                                    borderRadius: 1.5,
                                                    flexShrink: 0,
                                                }}
                                                imgSx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                }}
                                            />
                                            <Box>
                                                <Typography fontSize={12.5} color="#55729f">
                                                    {timestampToHumanReadableDate(post.timestamp)}
                                                </Typography>
                                                <Typography fontSize={11.5} color="#4d72a9" fontWeight={700}>
                                                    {post.contentType === 'photo_collection' ? 'PHOTO COLLECTION' : 'JOURNAL'}
                                                </Typography>
                                                <Typography color="#163f86" fontWeight={600} fontSize={15.5}>
                                                    {post.title}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))
                                )}
                            </Stack>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                p: 2.5,
                                borderRadius: 3,
                                backgroundColor: '#f7fbff',
                                border: '1px solid #d9e7f8',
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" color="#163d81" fontWeight={700}>
                                    Featured photos
                                </Typography>
                                <Button component={Link} to={`/${FEATURED_PHOTOS_PATH}`} size="small">
                                    See all
                                </Button>
                            </Box>
                            {featuredPhotosError ? (
                                <Typography color="#55729f" fontSize={14}>
                                    Could not load featured photos right now.
                                </Typography>
                            ) : (
                                <Grid container spacing={1.2}>
                                    {featuredPhotos.map((image, index) => (
                                        <Grid item xs={4} key={`${image.id}-${index}`}>
                                            <LoadingImage
                                                src={image.imageUrl}
                                                alt={`Featured photo ${index + 1}`}
                                                frameSx={{
                                                    width: '100%',
                                                    pt: '100%',
                                                    borderRadius: 2,
                                                }}
                                                imgSx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    objectPosition: `${image.squareFocalPointX ?? image.focalPointX ?? 50}% ${image.squareFocalPointY ?? image.focalPointY ?? 50}%`,
                                                    display: 'block',
                                                }}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    </Grid>
                </Grid>

            </Box>
        </Box>
    );
}
